import {
  Box,
  Checkbox,
  Divider,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import * as fp from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { IoCashOutline, IoPersonOutline, IoRestaurantOutline } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import BackwardBar from '~/components/BackwardBar';
import CouponsModal from '~/components/CouponsModal';
import FooterReturnNotice from '~/components/FooterReturnNotice';
import LocationBar from '~/components/LocationBar';
import SettlementSection from '~/components/SettlementSection';
import StoreLastPreOrderTimeNotice from '~/components/StoreLastPreOrderTimeNotice';
import TzWalletModal from '~/components/TzWalletModal';
import Content from '~/containers/Content';
import PageContainer from '~/containers/PageContainer';
import SectionContainer from '~/containers/SectionContainer';
import updateCart from '~/firebase/update/updateCart';
import { PLATFORM_ID } from '~/globalVariables';
import useCartId from '~/hooks/useCartId';
import useCheckout, { ErrorType } from '~/pages/Cart/hooks/useCheckout';
import useCouponCheck from '~/pages/Cart/hooks/useCouponCheck';
import useCoupons from '~/pages/Cart/hooks/useCoupons';
import usePaymentType from '~/pages/Cart/hooks/usePaymentType';
import useTaxInfo from '~/pages/Cart/hooks/useTaxInfo';
import useTaxType from '~/pages/Cart/hooks/useTaxType';
import authAtom from '~/recoil/atom/auth';
import customerAtom from '~/recoil/atom/customer';
import globalEnterpriseGroupBuyAtom from '~/recoil/atom/enterpriseGroupBuy/globalEnterpriseGroupBuyAtom';
import platformAtom from '~/recoil/atom/platform';
import cartSelector from '~/recoil/selectors/cart';
import cartSettlementSelector from '~/recoil/selectors/cartSettlement';
import isHostSelector from '~/recoil/selectors/isHost';
import { CartType, TaxType } from '~/types/cart';
import { PaymentType } from '~/types/common';
import { EnterpriseGroupBuyPaymentType } from '~/types/enterpriseGroupBuy';
import getPath from '~/utils/getNavigatePath';
import transformCouponDiscountString from '~/utils/transformCouponDiscountString';
import transformDateRangeToString from '~/utils/transformDateRangeToString';
import {
  CheckoutBar,
  CheckoutErrorModal,
  CompanyTaxForm,
  ConcatInfoSection,
  ConfirmCartModal,
  CouponBar,
  CouponInfoBar,
  InvalidOrdersModal,
  PaymentTypeRadioGroup,
  RedirectingModal,
  StoreItemsList,
} from './components';
import useEmail from './hooks/useEmail';
import usePhoneNumber from './hooks/usePhoneNumber';

export default function Cart() {
  const platform = useRecoilValue(platformAtom);
  const auth = useRecoilValue(authAtom);
  const customer = useRecoilValue(customerAtom);
  const isHost = useRecoilValue(isHostSelector);
  const cart = useRecoilValue(cartSelector);
  const { data } = useRecoilValue(globalEnterpriseGroupBuyAtom);
  const cartSettlement = useRecoilValue(cartSettlementSelector);
  const { cartId } = useCartId();
  const { groupId = '', enterpriseGroupBuyId = '' } = useParams();

  const navigate = useNavigate();

  const {
    displayAvailableCoupons,
    displayNotAvailableCoupons,
    notDisplayAvailableCoupons,
    notDisplayNotAvailableCoupons,
  } = useCoupons(auth);

  const {
    isOpen: isOpenTzWalletModal,
    onOpen: onOpenTzWalletModal,
    onClose: onCloseTzWalletModal,
  } = useDisclosure();

  const {
    isOpen: isCouponsModalOpen,
    onClose: onCouponsModalClose,
    onOpen: onCouponsModalOpen,
  } = useDisclosure();

  const {
    isLoading,
    isRedirecting,
    checkoutError,
    checkoutErrorOrders,
    invalidStoreIds,
    confirmModal,
    handleCheckout,
    handleClearError,
    handleClearOrdersError,
  } = useCheckout();

  const { phone, phoneError, handleChangePhone } = usePhoneNumber();
  const { emailError } = useEmail(cart.email);
  const { taxType, handleChangeTaxType } = useTaxType();
  const { paymentType, handleChangePaymentType } = usePaymentType(cart.paymentType);
  const {
    taxInfoState: { taxInfo, errors },
    handleChangeTitle,
    handleChangeInvoice,
    handleChangeTaxInfo,
  } = useTaxInfo(cart.taxInfo);

  const isOrderer = fp.eq(cart.cartType, CartType.CART_PERSONAL) || isHost;

  useCouponCheck(isOrderer);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const handleOpenTzWalletModal = () => {
    onOpenTzWalletModal();
  };

  const handleCheckoutClick = () => {
    // if (emailError && paymentType !== PaymentType.TZ_WALLET) {
    //   setShowError(true);
    //   return;
    // }
    confirmModal.onOpen();
  };

  const handleRemoveCoupon = async () => {
    try {
      await updateCart(PLATFORM_ID, cartId, {
        coupon: null,
        campaignId: '',
      });
    } catch (err) { }
  };

  const displaySettlementList = useMemo(() => {
    return [{ label: '餐點總金額', price: cartSettlement.subtotal }].concat(
      !fp.eq(cartSettlement.coupon, 0)
        ? [{ label: '使用優惠券', price: -cartSettlement.coupon }]
        : [],
    );
  }, [cartSettlement]);

  const paymentTypes = useMemo(() => {
    if (!!enterpriseGroupBuyId) {
      return data?.paymentType === EnterpriseGroupBuyPaymentType.CONSUMER
        ? ((data?.paymentSubType || []) as Array<any>)
        : [PaymentType.PLATFORM];
    }
    return platform.paymentTypes;
  }, [enterpriseGroupBuyId, platform.paymentTypes, data?.paymentSubType, data?.paymentType]);

  const isCheckoutButtonDisabled = useMemo(() => {
    if (!!emailError || !!phoneError) return true;
    if (!enterpriseGroupBuyId) {
      return !!(taxType === TaxType.COMPANY && (!taxInfo.invoice || !taxInfo.title));
    } else
      return !!(
        taxType === TaxType.COMPANY &&
        data?.paymentType === EnterpriseGroupBuyPaymentType.CONSUMER &&
        (!taxInfo.invoice || !taxInfo.title)
      );
  }, [
    emailError,
    phoneError,
    taxType,
    taxInfo?.invoice,
    taxInfo?.title,
    enterpriseGroupBuyId,
    data?.paymentType,
    data?.invoice?.enable,
  ]);

  return (
    <PageContainer
      hideHeader
      hideNavBar
      hideFooter
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => navigate(getPath('/', groupId, enterpriseGroupBuyId))}
          />
          <Heading py={1} size="md" bgColor="white" textAlign="center">
            訂單
          </Heading>
          <LocationBar />
        </>
      }
    >
      <Box bgColor="white" px={4}>
        <Content py={5} minH="150vh">
          <Stack
            align={{ base: 'stretch', md: 'flex-start' }}
            direction={{ base: 'column', md: 'row' }}
            spacing={5}
          >
            <VStack flex="1" align="stretch" spacing={5}>
              {/** pick meal time section */}
              <SectionContainer
                title="取餐時間區間"
                icon={<Icon as={AiOutlineClockCircle} boxSize="1.5rem" color="secondary" />}
              >
                <HStack justify="space-between" p={2}>
                  <Text fontWeight="bold">
                    {transformDateRangeToString(cart.pickMealAt, cart.pickMealEnd)}
                  </Text>
                </HStack>
              </SectionContainer>

              <Divider borderColor="gray.200" />

              {/** pick meal list section */}
              <SectionContainer
                title="餐點"
                icon={<Icon as={IoRestaurantOutline} boxSize="1.5rem" color="secondary" />}
              >
                <StoreItemsList orders={cart.orders} invalidStoreIds={invalidStoreIds} />
              </SectionContainer>

              {isOrderer && (
                <SectionContainer
                  title="聯絡資訊"
                  icon={<Icon as={IoPersonOutline} boxSize="1.5rem" color="secondary" />}
                >
                  <ConcatInfoSection
                    isAuthMissingPhone={!customer?.phoneNumber && !auth?.phoneNumber}
                    name={customer?.displayName || auth?.displayName || ''}
                    phone={phone || customer?.phoneNumber || auth?.phoneNumber || ''}
                    phoneError={phoneError}
                    onChangePhone={handleChangePhone}
                  />
                </SectionContainer>
              )}

              {!!enterpriseGroupBuyId &&
                data?.paymentType !== EnterpriseGroupBuyPaymentType.CONSUMER ? null : (
                <SectionContainer
                  title={
                    <HStack align="center" spacing="8px">
                      <Checkbox
                        size="lg"
                        isChecked={taxType === TaxType.COMPANY}
                        onChange={() => {
                          if (taxType === TaxType.COMPANY) {
                            handleChangeTaxInfo({ invoice: '', title: '' });
                          }
                          handleChangeTaxType?.(
                            taxType === TaxType.PERSONAL ? TaxType.COMPANY : TaxType.PERSONAL,
                          );
                        }}
                      />
                      <Text fontSize="sm" fontWeight={400}>
                        需要統編
                      </Text>
                    </HStack>
                  }
                >
                  {fp.eq(taxType, TaxType.COMPANY) ? (
                    <VStack spacing="16px" marginTop="16px">
                      <CompanyTaxForm
                        taxInfo={taxInfo}
                        errors={errors}
                        onChangeTaxInvoice={handleChangeInvoice}
                        onChangeTaxTitle={handleChangeTitle}
                      />

                      <Text fontSize="xs">
                        weserve 點餐系統僅提供發票資訊給店家，實際開立發票方式由店家決定。
                      </Text>
                    </VStack>
                  ) : null}
                </SectionContainer>
              )}

              <Divider borderColor="gray.200" />
            </VStack>

            <VStack flex="1" align="stretch" spacing={5} pos="sticky" top="150px">
              {/** payment type selector section */}
              {isOrderer && (
                <SectionContainer
                  title="付款方式"
                  icon={<Icon as={IoCashOutline} boxSize="1.5rem" color="secondary" />}
                >
                  <PaymentTypeRadioGroup
                    paymentTypes={paymentTypes}
                    paymentType={paymentType}
                    onChangePaymentType={handleChangePaymentType}
                    onOpenTzWalletModal={handleOpenTzWalletModal}
                  />
                </SectionContainer>
              )}
              {!enterpriseGroupBuyId && isOrderer && (
                <CouponBar
                  availableCouponAmount={displayAvailableCoupons.length}
                  displayCouponAmount={
                    displayAvailableCoupons.length + displayNotAvailableCoupons.length
                  }
                  onClick={onCouponsModalOpen}
                />
              )}
              {!enterpriseGroupBuyId && isOrderer && cart.coupon && (
                <CouponInfoBar
                  value={transformCouponDiscountString(cart.coupon.type, cart.coupon.condition)}
                  onClick={handleRemoveCoupon}
                />
              )}
              <SettlementSection displayList={displaySettlementList} total={cartSettlement.total} />

              <Text fontSize="xs">
                若消費者在店家確認接單後棄單，店家擁有選擇不退款或封鎖該客戶之權利。
              </Text>
              <Box display={{ base: 'none', md: 'block' }}>
                {!!enterpriseGroupBuyId && (
                  <>
                    {data?.orderEndAt ? (
                      <StoreLastPreOrderTimeNotice
                        isEnterpriseGroupBuy
                        isValidTime
                        time={data?.orderEndAt.toDate()}
                      />
                    ) : null}
                  </>
                )}
                <FooterReturnNotice />
                <CheckoutBar
                  disabled={isCheckoutButtonDisabled}
                  isOrderer={isOrderer}
                  isLoading={isLoading}
                  total={cartSettlement.total}
                  onClick={handleCheckoutClick}
                />
              </Box>
            </VStack>
          </Stack>
        </Content>
      </Box>
      <Box pos="sticky" bottom="0" display={{ base: 'block', md: 'none' }}>
        {!!enterpriseGroupBuyId && (
          <>
            {data?.orderEndAt ? (
              <StoreLastPreOrderTimeNotice
                isEnterpriseGroupBuy
                isValidTime
                time={data?.orderEndAt.toDate()}
              />
            ) : null}
          </>
        )}
        <FooterReturnNotice />
        <CheckoutBar
          disabled={isCheckoutButtonDisabled}
          isOrderer={isOrderer}
          isLoading={isLoading}
          total={cartSettlement.total}
          onClick={handleCheckoutClick}
        />
      </Box>

      <CouponsModal
        isOpen={isCouponsModalOpen}
        onClose={onCouponsModalClose}
        displayAvailableCoupons={displayAvailableCoupons}
        displayNotAvailableCoupons={displayNotAvailableCoupons}
        notDisplayAvailableCoupons={notDisplayAvailableCoupons}
        notDisplayNotAvailableCoupons={notDisplayNotAvailableCoupons}
      />
      <ConfirmCartModal
        pickMealTime={transformDateRangeToString(cart.pickMealAt, cart.pickMealEnd)}
        pickMealPoint={
          !!enterpriseGroupBuyId ? data?.addressSpec || '' : cart.pickMealPoint?.name || ''
        }
        orders={cart.orders}
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.onClose}
        onConfirm={handleCheckout}
      />
      <CheckoutErrorModal
        isOpen={!fp.eq(checkoutError.type, ErrorType.NONE)}
        title={checkoutError.title}
        message={checkoutError.message}
        onClose={handleClearError}
      />
      <TzWalletModal isOpen={isOpenTzWalletModal} onClose={onCloseTzWalletModal} />

      <InvalidOrdersModal
        isOpen={!fp.eq(checkoutErrorOrders.type, ErrorType.NONE)}
        invalidOrders={checkoutErrorOrders.invalidOrders}
        onClose={() => { }}
        onConfirm={handleClearOrdersError}
        cartType={cart.cartType}
      />
      <RedirectingModal isOpen={isRedirecting} />
    </PageContainer>
  );
}
